/**
 * The weather of a map, rolled from the map seed. The client draws it (Battlefield look) and the
 * server uses it for fire (rain shortens fires and stops them spreading on grass), so both must
 * roll exactly the same way.
 */
import { FloorNames } from "./terrain";

export type WeatherName = "overcast" | "drizzle" | "rain" | "fog";

/**
 * The sky a match is played under (1.05, owner decision, 28 October 2026: "add the new night, snow, thunderstoirm and
 * dusk modes, also can choose which in quick start (for our dev mode), in game, they're random 1/5% chance each will
 * play"): a clear day (the rolled weather above, as before), dusk, night (flashlights), snow or a thunderstorm. The
 * server picks it as a match is made (`rollSky`, one in five each unless its config says otherwise: `skies`), a
 * development Quick start can choose it, and the map packet carries it to the players.
 */
export type SkyName = "clear" | "dusk" | "night" | "snow" | "storm";

/** every sky, in the map packet's order (its byte is the index here: append, never reorder) */
export const SKY_NAMES: readonly SkyName[] = Object.freeze(["clear", "dusk", "night", "snow", "storm"]);

/** each sky's share of the matches, out of their total: one in five each */
export const DEFAULT_SKY_CHANCES: Readonly<Record<SkyName, number>> = Object.freeze({ clear: 1, dusk: 1, night: 1, snow: 1, storm: 1 });

/**
 * each sky's share of the matches from a server's config (`skies`): a sky it leaves out keeps its share of 1, so one that
 * says nothing plays each one in five
 */
export const skyChances = (configured?: Readonly<Partial<Record<SkyName, unknown>>>): Readonly<Partial<Record<SkyName, unknown>>> => ({
    ...DEFAULT_SKY_CHANCES,
    ...configured
});

export const isSkyName = (value: unknown): value is SkyName => typeof value === "string" && (SKY_NAMES as readonly string[]).includes(value);

/**
 * the floor the water is under a sky (0.98.5, owner decision, 1 November 2026: "the river should be freezed, like an
 * iced water surface"): ice in the snow (walked on, slid on, no splash: utils/terrain.ts `FloorTypes.ice`), water under
 * every other sky. The rivers', the sea's and a building's water floor's (Pool Day's pool), on the server and the page
 * alike (the map's `waterType`)
 */
export const waterOfSky = (sky: SkyName | undefined): FloorNames => sky === "snow" ? FloorNames.Ice : FloorNames.Water;

/**
 * A sky by its chances: `random` in [0, 1) picks one in proportion to its share. A share that isn't a finite number of
 * 0 or more counts as 0 (a config's typo never breaks a match); every share 0, a clear sky.
 */
export function rollSky(random: number, chances: Readonly<Partial<Record<SkyName, unknown>>> = DEFAULT_SKY_CHANCES): SkyName {
    const share = (sky: SkyName): number => {
        const value = chances[sky];
        return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : 0;
    };
    const total = SKY_NAMES.reduce((sum, sky) => sum + share(sky), 0);
    if (!(total > 0)) return "clear";
    let left = Math.min(Math.max(Number.isFinite(random) ? random : 0, 0), 0.999_999_999) * total;
    for (const sky of SKY_NAMES) {
        const part = share(sky);
        if (part <= 0) continue;
        if (left < part) return sky;
        left -= part;
    }
    // (rounding at the very end: the last sky with a share)
    return [...SKY_NAMES].reverse().find(sky => share(sky) > 0) ?? "clear";
}

export interface MapWeather {
    /** the weather fire follows (definitions/fire.ts `weather`): snow and a thunderstorm are wet, as rain is */
    readonly name: WeatherName
    readonly fog: number
    /** 0 dry, 0.55 drizzle, 1 rain (and a thunderstorm) */
    readonly rain: number
    /** 0, or 1 under snow: the ground white, snow falling */
    readonly snow: number
    readonly windAngle: number
    readonly windSpeed: number
    /** the match's sky ("clear" for the rolled weather) */
    readonly sky: SkyName
}

/** a small seeded random generator (mulberry32) */
export function mulberry32(seed: number): () => number {
    let s = seed | 0;
    return () => {
        s = (s + 0x6D2B79F5) | 0;
        let t = Math.imul(s ^ (s >>> 15), s | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * A match's weather: under a clear sky the one rolled from the seed (overcast, drizzle, rain or fog, as before 1.05);
 * under the others their own (dusk and night dry, snow and a thunderstorm wet). The wind is the seed's in every sky.
 */
export function mapWeather(seed: number, sky: SkyName = "clear"): MapWeather {
    const rng = mulberry32(seed ^ 0x5eed);
    const roll = rng();
    const windAngle = rng() * Math.PI * 2;
    const windSpeed = 0.7 + rng() * 0.9;
    switch (sky) {
        case "dusk": return { name: "overcast", fog: 0.1, rain: 0, snow: 0, windAngle, windSpeed: windSpeed * 0.7, sky };
        // (no fog at night: the page draws it over the dark, where it would glow)
        case "night": return { name: "overcast", fog: 0, rain: 0, snow: 0, windAngle, windSpeed: windSpeed * 0.6, sky };
        case "snow": return { name: "rain", fog: 0.2, rain: 0, snow: 1, windAngle, windSpeed: windSpeed * 0.8, sky };
        case "storm": return { name: "rain", fog: 0.26, rain: 1, snow: 0, windAngle, windSpeed: windSpeed * 1.5, sky };
        default: break;
    }
    if (roll < 0.42) return { name: "overcast", fog: 0.14, rain: 0, snow: 0, windAngle, windSpeed, sky: "clear" };
    if (roll < 0.72) return { name: "drizzle", fog: 0.18, rain: 0.55, snow: 0, windAngle, windSpeed, sky: "clear" };
    if (roll < 0.87) return { name: "rain", fog: 0.22, rain: 1, snow: 0, windAngle, windSpeed, sky: "clear" };
    return { name: "fog", fog: 0.34, rain: 0, snow: 0, windAngle, windSpeed, sky: "clear" };
}
